import { useState, useCallback } from 'react';
import {
  generateShareKey, exportKeyRaw, wrapKey, unwrapKey, decryptBuffer, deriveKey, generateSalt, encryptForShare,
  decodeShareName, base64ToBuffer, toUrlSafeBase64
} from '../utils/crypto';
import {
  apiSharesList, apiSharesCreate, apiSharesDelete, apiShareAddItem,
  apiDownloadFile, marcaVersione, apiListFiles
} from '../utils/api';
import { useAuth } from './useAuth';

export function useShares() {
  const { encKey } = useAuth();
  const [shares, setShares]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState({ done: 0, total: 0 });

  const loadShares = useCallback(async () => {
    setLoading(true);
    try { setShares(await apiSharesList()); } finally { setLoading(false); }
  }, []);

  // Recursively gather { file, relPath } for every file under a folder,
  // using the already-loaded folder tree to walk subfolders.
  async function collectFolderFiles(folder, allFolders, prefix = '') {
    const relPrefix = prefix ? `${prefix}/${folder.name}` : folder.name;
    const files = await apiListFiles(folder.id);
    const out = files.map(f => ({ file: f, relPath: relPrefix }));
    const children = allFolders.filter(f => f.parentId === folder.id);
    for (const child of children) {
      out.push(...await collectFolderFiles(child, allFolders, relPrefix));
    }
    return out;
  }

  // Resolve a vault file's display name — same "plain:<b64>" convention used
  // everywhere else, with the encKey-decrypt fallback for pre-v2.1 uploads.
  async function resolveVaultFilename(file) {
    if (!file.encrypted) return file.plainName || 'file';
    if (!file.encryptedName) return file.plainName || 'file';
    if (file.encryptedName.startsWith('plain:')) return decodeShareName(file.encryptedName);
    try {
      const [nameIvB64, nameCipherB64] = file.encryptedName.split(':');
      const buf = await decryptBuffer(encKey, base64ToBuffer(nameCipherB64), nameIvB64);
      return new TextDecoder().decode(buf);
    } catch { return file.plainName || 'file'; }
  }

  // Re-encrypt one already-fetched vault file for the share and upload it.
  async function shareOneFile(shareId, shareKey, file, relPathDir) {
    const { blob, isEncrypted } = await apiDownloadFile(file.id, marcaVersione(file));
    const filename = await resolveVaultFilename(file);
    let plainBuffer;
    if (isEncrypted) {
      if (!encKey) throw new Error('No encryption key — please log in again');
      // DUE FORMATI, SI SCEGLIE DALLA RIGA (2026-09-18).
      //
      // Dal 13/09 i caricamenti sono a blocchi (format_version=2). Qui c'era
      // il solo decryptBuffer, cioe' AES-GCM sul file intero: su un file a
      // blocchi WebCrypto lanciava un OperationError con messaggio VUOTO, la
      // finestra non mostrava niente e il link nasceva senza contenuto — 26
      // link vuoti fra il 13 e il 18/09, di sette utenti. Si usa lo stesso
      // lettore del vault (decifraV2InMemoria, la funzione che leggono anche
      // VaultPage e ImageThumb), scelto da formatVersion come in
      // useFiles.downloadFile — mai dai primi byte, che in un file vecchio
      // possono valere "LCB2" per caso. formatVersion assente = formato di
      // sempre, che continua a passare di qui sotto.
      if (Number(file.formatVersion) === 2) {
        const { decifraV2InMemoria } = await import('../utils/cifraturaBlocchi');
        const inChiaro = await decifraV2InMemoria(blob, encKey, file.mimeType || '');
        plainBuffer = await inChiaro.arrayBuffer();
      } else {
        plainBuffer = await decryptBuffer(encKey, await blob.arrayBuffer(), file.iv);
      }
    } else {
      plainBuffer = await blob.arrayBuffer();
    }
    const enc = await encryptForShare(shareKey, plainBuffer, filename);
    const relPath = relPathDir ? `${relPathDir}/${filename}` : filename;
    await apiShareAddItem(shareId, {
      blob: enc.blob, relPath, iv: enc.iv, encryptedName: enc.encryptedName,
      size: plainBuffer.byteLength, mimeType: file.mimeType
    });
  }

  /**
   * Create a shareable link for a file or folder.
   * target: { type: 'file'|'folder', id, name }
   * opts: { password?, expiresAt?, maxDownloads?, allFolders? (needed for folder shares) }
   * Returns { id, link } — link already includes the #k= fragment when unprotected.
   */
  const createShare = useCallback(async (target, opts = {}) => {
    if (!encKey) throw new Error('No encryption key — please log in again');
    setCreating(true); setCreateProgress({ done: 0, total: 0 });
    try {
      const shareKey = await generateShareKey();
      const ownerWrappedKey = await wrapKey(shareKey, encKey);

      let hasPassword = false, pwSalt = null, wrappedKey = null;
      if (opts.password) {
        hasPassword = true;
        pwSalt = generateSalt();
        const pwKey = await deriveKey(opts.password, pwSalt);
        wrappedKey = await wrapKey(shareKey, pwKey);
      }

      const { id } = await apiSharesCreate({
        type: target.type, sourceId: target.id, name: target.name,
        hasPassword, pwSalt, wrappedKey, ownerWrappedKey,
        expiresAt: opts.expiresAt || null,
        maxDownloads: opts.maxDownloads || null
      });

      if (target.type === 'file') {
        setCreateProgress({ done: 0, total: 1 });
        await shareOneFile(id, shareKey, target.raw, '');
        setCreateProgress({ done: 1, total: 1 });
      } else {
        const items = await collectFolderFiles(target.raw, opts.allFolders || []);
        setCreateProgress({ done: 0, total: items.length });
        for (const { file, relPath } of items) {
          await shareOneFile(id, shareKey, file, relPath === target.name ? '' : relPath.slice(target.name.length + 1));
          setCreateProgress(p => ({ ...p, done: p.done + 1 }));
        }
      }

      const rawKeyB64 = toUrlSafeBase64(await exportKeyRaw(shareKey));
      const base = `${window.location.origin}/s/${id}`;
      const link = hasPassword ? base : `${base}#k=${rawKeyB64}`;
      await loadShares();
      return { id, link };
    } finally {
      setCreating(false);
    }
  }, [encKey, loadShares]);

  // Rebuild the copyable link for an existing share from its owner-wrapped key
  const getShareLink = useCallback(async (share) => {
    const base = `${window.location.origin}/s/${share.id}`;
    if (share.hasPassword) return base;
    if (!encKey || !share.ownerWrappedKey) return base;
    const shareKey = await unwrapKey(share.ownerWrappedKey, encKey);
    const rawKeyB64 = toUrlSafeBase64(await exportKeyRaw(shareKey));
    return `${base}#k=${rawKeyB64}`;
  }, [encKey]);

  const revokeShare = useCallback(async (id) => {
    await apiSharesDelete(id);
    setShares(prev => prev.filter(s => s.id !== id));
  }, []);

  return { shares, loading, creating, createProgress, loadShares, createShare, getShareLink, revokeShare };
}
